<script lang="ts" setup>
import InvestorHero from '@/domains/investor/components/Hero/Hero.vue'
import { useRecommendationPage } from '@/domains/investor/composables/useRecommendation'
import type { IAdaptRecommendation } from '@/types/investor'
import RecommendationTable from '@/domains/investor/components/RecommendationTable/RecommendationTable.vue'
import RecommendationCards from '@/domains/investor/components/RecommendationCards/RecommendationCards.vue'
import AnalyticsTable from '@/domains/investor/components/AnalyticsTable/AnalyticsTable.vue'
import AnalyticsCards from '@/domains/investor/components/AnalyticsCards/AnalyticsCards.vue'
import { queryParam, useQueryState } from '@/domains/investor/composables/useQueryState'

const ITEMS_PER_PAGE = 10
const { page } = useQueryState({ page: queryParam.page() })
const recommendations = ref<IAdaptRecommendation[]>([])
const total = ref(0)
const { fetchRecommendationPage, fetchAnalyticsTable } = await useRecommendationPage()
const table = await fetchAnalyticsTable()

const { isTrueDesktop } = useBSBreakpoints()

watch(page, async (newPage) => {
  const data = await fetchRecommendationPage(newPage)
  recommendations.value = data.value?.recommendations || []
  total.value = data.value?.total || 0
}, { immediate: true })

definePageMeta({
  layout: 'investor',
})

const { t, locale } = useI18n()

useHead(() => ({
  title: t('pageTitles.investorStockRecommendation.title'),
  meta: [
    {
      name: 'description',
      content: t('pageTitles.investorStockRecommendation.meta'),
    },
  ],
  link: [
    {
      rel: 'canonical',
      href: t(`pageTitles.investorStockRecommendation.canonical.${locale.value}`),
    },
    {
      rel: 'alternate',
      hreflang: 'pl',
      href: t('pageTitles.investorStockRecommendation.canonical.pl'),
    },
    {
      rel: 'alternate',
      hreflang: 'en',
      href: t('pageTitles.investorStockRecommendation.canonical.en'),
    },
    {
      rel: 'alternate',
      hreflang: 'x-default',
      href: t('pageTitles.investorStockRecommendation.canonical.pl'),
    },
  ],
}))
</script>

<template>
  <div class="investor-stock-recommendation-page">
    <InvestorHero
      class="investor-stock-recommendation-page__hero"
      :title="$t('investor.stock.recommendation.title')"
      :lead="$t('investor.stock.recommendation.description')"
      :main-category="{
        label: $t('investor.stock.recommendation.mainCategory.label'),
        url: $t('investor.stock.recommendation.mainCategory.url'),
      }"
      :second-category="{
        label: $t('investor.stock.recommendation.secondCategory.label'),
        url: $t('investor.stock.recommendation.secondCategory.url'),
      }"
      :first-button="{ text: $t('investor.stock.recommendation.firstButton.text'), to: $t('investor.stock.recommendation.firstButton.to') }"
      image="/images/investor/stock/stock_recommendation_banner.png"
    />

    <AnalyticsCards
      v-if="!isTrueDesktop && table?.table"
      :table="table.table"
    />
    
    <AnalyticsTable
      v-if="isTrueDesktop && table?.table"
      :table="table?.table"
    />

    <RecommendationCards
      v-if="!isTrueDesktop && recommendations?.length > 0"
      :id="$t('investor.stock.recommendation.recommendationSectionId')"
      v-model="page"
      :page="page"
      :items="recommendations"
      :total="total"
      :items-per-page="ITEMS_PER_PAGE"
    />

    <RecommendationTable
      v-if="isTrueDesktop && recommendations?.length > 0"
      :id="$t('investor.stock.recommendation.recommendationSectionId')"
      v-model="page"
      :page="page"
      :items="recommendations"
      :total="total"
      :items-per-page="ITEMS_PER_PAGE"
    />
  </div>
</template>

<style scoped lang="scss">
.investor-stock-recommendation-page {
  :deep(.investorHero__title) {
    @include h7;
  }

  :deep(.analytics-cards__title) {
    @include h7;
  }

  :deep(.investor-stock-recommendation-cards__title) {
    @include h7;
  }

  @include atXS {
    :deep(.investorHero__title) {
      @include h5;
    }

    :deep(.analytics-cards__title) {
      @include h5;
    }

    :deep(.investor-stock-recommendation-cards__title) {
      @include h5;
    }
  }
}
</style>
